import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../lib/api';
import {
  BudgetDisplayContext,
  BudgetFetchParams,
  BudgetItemsResponse,
  WorkspacePermissions,
} from '../types/budget';

interface UseBudgetDisplayProps {
  context: BudgetDisplayContext;
  workspaceId?: string;
  permissions?: WorkspacePermissions;
}

export const useBudgetDisplay = ({ context, workspaceId, permissions }: UseBudgetDisplayProps) => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useState<BudgetFetchParams>({
    sortBy: 'item_number',
    sortDirection: 'asc',
  });
  const [viewMode, setViewMode] = useState<'card' | 'table'>('table');
  const [showingAll, setShowingAll] = useState(false);

  const queryKey = ['budget-items', context, workspaceId, searchParams];

  const { data: itemsData, isLoading, isFetching, error, refetch } = useQuery<BudgetItemsResponse>({
    queryKey,
    queryFn: async () => {
      const params: BudgetFetchParams = {
        ...searchParams,
        ...(context === 'workspace' && workspaceId && { workspace_id: workspaceId, tracked_only: 'true' }),
      };
      const response = await api.get('/budget/items', { params });
      return response.data;
    },
    enabled: context === 'budget' || (context === 'workspace' && !!workspaceId),
    staleTime: 30000,
    keepPreviousData: true,
  });

  const items = itemsData?.data?.items || [];
  const total = itemsData?.data?.total || 0;
  const hasMore = itemsData?.data?.hasMore || false;

  const updateSearchParams = useCallback((newParams: Partial<BudgetFetchParams>) => {
    setSearchParams(prev => ({
      ...prev,
      ...newParams,
      offset: newParams.offset !== undefined ? newParams.offset : 0,
    }));
  }, []);

  const loadMore = useCallback(() => {
    if (hasMore && !isLoading) {
      const currentOffset = searchParams.offset || 0;
      const limit = searchParams.limit || 50;
      updateSearchParams({ offset: currentOffset + limit });
    }
  }, [hasMore, isLoading, searchParams.offset, searchParams.limit, updateSearchParams]);

  const handleShowAll = useCallback(() => {
    if (total > 0) {
      setShowingAll(true);
      updateSearchParams({ limit: total, offset: 0 });
    }
  }, [total, updateSearchParams]);

  const handleShowPaginated = useCallback(() => {
    setShowingAll(false);
    updateSearchParams({ limit: 50, offset: 0 });
  }, [updateSearchParams]);

  // Position mutation (workspace context)
  const updatePositionMutation = useMutation({
    mutationFn: async ({ itemId, position }: { itemId: string; position: string }) => {
      if (context !== 'workspace' || !workspaceId) {
        throw new Error('Position updates only available in workspace context');
      }
      const response = await api.put(`/workspace-budget-tracking/position/${workspaceId}/${itemId}`, { position });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['budget-items', context, workspaceId]);
      toast.success('Budget item position updated');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update position');
    },
  });

  // Hot toggle mutation with optimistic update
  const toggleHotMutation = useMutation({
    mutationFn: async ({ itemId, isHot }: { itemId: string; isHot: boolean }) => {
      if (context !== 'workspace' || !workspaceId) {
        throw new Error('Hot toggle only available in workspace context');
      }
      const response = await api.put(`/workspace-budget-tracking/hot/${workspaceId}/${itemId}`, { isHot });
      return response.data;
    },
    onMutate: async ({ itemId, isHot }) => {
      await queryClient.cancelQueries(['budget-items', context, workspaceId]);

      const previousQueries: Array<{ key: any; data: unknown }> = [];
      queryClient.getQueriesData(['budget-items', context, workspaceId]).forEach(([key, data]) => {
        previousQueries.push({ key, data });
        if (data && typeof data === 'object' && 'data' in (data as any)) {
          const d = data as BudgetItemsResponse;
          const updatedItems = d.data.items.map((i: any) =>
            String(i.id) === String(itemId) ? { ...i, isHot, tracked_is_hot: isHot } : i
          );
          queryClient.setQueryData(key, { ...d, data: { ...d.data, items: updatedItems } });
        }
      });
      return { previousQueries };
    },
    onError: (error: any, _vars, ctx) => {
      ctx?.previousQueries.forEach(({ key, data }) => queryClient.setQueryData(key, data));
      toast.error(error.response?.data?.message || 'Failed to toggle hot status');
    },
    onSettled: () => {
      queryClient.invalidateQueries(['budget-items', context, workspaceId]);
    },
    onSuccess: () => {
      toast.success('Hot status updated');
    },
  });

  // Review toggle mutation
  const toggleReviewMutation = useMutation({
    mutationFn: async ({ itemId, needsClientReview }: { itemId: string; needsClientReview: boolean }) => {
      if (context !== 'workspace' || !workspaceId) {
        throw new Error('Review toggle only available in workspace context');
      }
      const response = await api.put(`/workspace-budget-tracking/review/${workspaceId}/${itemId}`, { needsClientReview });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['budget-items', context, workspaceId]);
      toast.success('Review flag updated');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to toggle review flag');
    },
  });

  // Remove item from workspace tracking
  const removeItemMutation = useMutation({
    mutationFn: async (itemId: string) => {
      if (context !== 'workspace' || !workspaceId) {
        throw new Error('Remove only available in workspace context');
      }
      const response = await api.delete(`/workspace-budget-tracking/${workspaceId}/${itemId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['budget-items', context, workspaceId]);
      toast.success('Budget item removed from workspace');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to remove budget item');
    },
  });

  const handleUpdatePosition = useCallback((itemId: string, position: string) => {
    if (permissions?.canEdit) {
      updatePositionMutation.mutate({ itemId, position });
    }
  }, [permissions?.canEdit, updatePositionMutation]);

  const handleToggleHot = useCallback((itemId: string, isHot: boolean) => {
    if (permissions?.canEdit) {
      toggleHotMutation.mutate({ itemId, isHot });
    }
  }, [permissions?.canEdit, toggleHotMutation]);

  const handleToggleReview = useCallback((itemId: string, needsClientReview: boolean) => {
    if (permissions?.canEdit) {
      toggleReviewMutation.mutate({ itemId, needsClientReview });
    }
  }, [permissions?.canEdit, toggleReviewMutation]);

  const handleRemoveItem = useCallback((itemId: string) => {
    if (permissions?.canEdit) {
      removeItemMutation.mutate(itemId);
    }
  }, [permissions?.canEdit, removeItemMutation]);

  const handleSort = useCallback((field: string, direction: 'asc' | 'desc') => {
    updateSearchParams({ sortBy: field, sortDirection: direction });
  }, [updateSearchParams]);

  const handleSearch = useCallback((searchQuery: string) => {
    updateSearchParams({ search: searchQuery });
  }, [updateSearchParams]);

  const handleFilter = useCallback((filters: Partial<BudgetFetchParams>) => {
    updateSearchParams(filters);
  }, [updateSearchParams]);

  return {
    items,
    total,
    hasMore,
    isLoading,
    isFetching,
    error,

    viewMode,
    setViewMode,
    searchParams,
    showingAll,

    updateSearchParams,
    loadMore,
    handleShowAll,
    handleShowPaginated,
    refetch,
    handleUpdatePosition,
    handleToggleHot,
    handleToggleReview,
    handleRemoveItem,
    handleSort,
    handleSearch,
    handleFilter,

    isUpdatingPosition: updatePositionMutation.isLoading,
    isTogglingHot: toggleHotMutation.isLoading,
    isTogglingReview: toggleReviewMutation.isLoading,
    isRemovingItem: removeItemMutation.isLoading,
  };
};
